// Core type definitions for Interactive Motion Art

/**
 * Pose landmark data from MediaPipe representing a single body point.
 * All coordinates are normalized to [0, 1] range for screen-independent positioning.
 * 
 * @property x - Horizontal position, normalized to [0, 1] where 0 is left edge, 1 is right edge
 * @property y - Vertical position, normalized to [0, 1] where 0 is top edge, 1 is bottom edge
 * @property z - Depth relative to hips (negative = closer to camera, positive = further away)
 * @property visibility - Confidence score [0, 1] indicating detection quality (>0.5 recommended for use)
 */
export interface PoseLandmark {
  x: number;
  y: number;
  z: number;
  visibility: number;
}

/**
 * Complete pose data for one person containing all tracked body landmarks.
 * Includes 17 key body points from head to ankles.
 * 
 * @property landmarks - Object containing all body landmark positions
 * @property timestamp - Time when pose was detected (milliseconds since epoch)
 * @property detected - Whether a person is currently detected in frame
 */
export interface PoseData {
  landmarks: {
    nose: PoseLandmark;
    leftEye: PoseLandmark;
    rightEye: PoseLandmark;
    leftEar: PoseLandmark;
    rightEar: PoseLandmark;
    leftShoulder: PoseLandmark;
    rightShoulder: PoseLandmark;
    leftElbow: PoseLandmark;
    rightElbow: PoseLandmark;
    leftWrist: PoseLandmark;
    rightWrist: PoseLandmark;
    leftHip: PoseLandmark;
    rightHip: PoseLandmark;
    leftKnee: PoseLandmark;
    rightKnee: PoseLandmark;
    leftAnkle: PoseLandmark;
    rightAnkle: PoseLandmark;
  };
  timestamp: number;
  detected: boolean;
}

/**
 * Person identifier (temporary, not persistent across exits/re-entries).
 * Used to track individuals across frames during a session.
 */
export type PersonID = string;

/**
 * Extended PoseData with person-specific metadata for multi-person tracking.
 * 
 * @property personId - Unique identifier for this person
 * @property zDepth - Average Z-depth for occlusion ordering (calculated from torso landmarks)
 */
export interface PersonPoseData extends PoseData {
  personId: PersonID;
  zDepth: number;
}

/**
 * Visualization types available for rendering.
 * Each person is assigned a random visualization type when first detected.
 */
export enum VisualizationType {
  PARTICLE_TRAILS = 'PARTICLE_TRAILS',
  GEOMETRIC_SHAPES = 'GEOMETRIC_SHAPES',
  FLOWING_RIBBONS = 'FLOWING_RIBBONS',
  DEPARTURE_BOARD = 'DEPARTURE_BOARD',
}

/**
 * Rendering scope of a visualization.
 *
 * - PER_PERSON: the visualization is drawn independently for each tracked person.
 *   The engine maintains one renderer instance per person and composites them
 *   over a shared background (see IArtisticRenderer).
 * - SCENE: the visualization is canvas-wide. The engine runs a single renderer
 *   once per frame, handing it all detected people, and the renderer owns the
 *   entire canvas including its background (see ISceneRenderer).
 *
 * Adding a new canvas-wide visualization is a matter of declaring its scope here
 * and registering a scene renderer; the engine routes by scope generically.
 */
export enum VisualizationScope {
  PER_PERSON = 'PER_PERSON',
  SCENE = 'SCENE',
}

/**
 * Scope for every visualization type. Single source of truth used by the
 * AnimationEngine to decide between per-person compositing and canvas-wide
 * scene rendering.
 */
export const VISUALIZATION_SCOPES: Record<VisualizationType, VisualizationScope> = {
  [VisualizationType.PARTICLE_TRAILS]: VisualizationScope.PER_PERSON,
  [VisualizationType.GEOMETRIC_SHAPES]: VisualizationScope.PER_PERSON,
  [VisualizationType.FLOWING_RIBBONS]: VisualizationScope.PER_PERSON,
  [VisualizationType.DEPARTURE_BOARD]: VisualizationScope.SCENE,
};

/**
 * Get the rendering scope for a visualization type.
 * Falls back to PER_PERSON for unknown types.
 */
export function getVisualizationScope(type: VisualizationType): VisualizationScope {
  return VISUALIZATION_SCOPES[type] ?? VisualizationScope.PER_PERSON;
}

/**
 * Ordered list of visualization types for cycling (manual selector and gesture switching).
 * Order defines the "next"/"previous" sequence.
 */
export const VISUALIZATION_TYPE_ORDER: VisualizationType[] = [
  VisualizationType.PARTICLE_TRAILS,
  VisualizationType.GEOMETRIC_SHAPES,
  VisualizationType.FLOWING_RIBBONS,
  VisualizationType.DEPARTURE_BOARD,
];

/**
 * Get the next (or previous) visualization type in the cycle, wrapping around.
 *
 * @param current The current visualization type
 * @param direction +1 for next, -1 for previous (default +1)
 * @returns The adjacent visualization type, wrapping at the ends
 */
export function cycleVisualizationType(
  current: VisualizationType,
  direction: 1 | -1 = 1
): VisualizationType {
  const order = VISUALIZATION_TYPE_ORDER;
  const currentIndex = order.indexOf(current);
  // If current isn't found, fall back to the first entry.
  const baseIndex = currentIndex === -1 ? 0 : currentIndex;
  const nextIndex = (baseIndex + direction + order.length) % order.length;
  return order[nextIndex] ?? order[0]!;
}

/**
 * Gesture state per person.
 * Tracks active gestures and debounce state independently for each person.
 * 
 * @property activeGestures - Set of currently detected gestures for this person
 * @property lastGestureTimes - Map of gesture types to last trigger timestamps for debouncing
 */
export interface GestureState {
  activeGestures: Set<GestureType>;
  lastGestureTimes: Map<GestureType, number>;
}

/**
 * Trail point for motion history visualization.
 * 
 * @property x - Horizontal position (normalized)
 * @property y - Vertical position (normalized)
 * @property timestamp - Time when point was recorded
 */
export interface TrailPoint {
  x: number;
  y: number;
  timestamp: number;
}

/**
 * Motion tracker for velocity and acceleration calculations.
 * Used for motion-responsive visual effects.
 * 
 * @property velocity - Current velocity vector
 * @property acceleration - Current acceleration vector
 * @property lastPosition - Previous position for delta calculations
 */
export interface MotionTracker {
  velocity: { x: number; y: number };
  acceleration: { x: number; y: number };
  lastPosition: { x: number; y: number };
}

/**
 * Complete data for one tracked person.
 * Contains all state needed for independent processing and rendering.
 * 
 * @property personId - Unique identifier for this person
 * @property poseData - Current pose landmarks and metadata
 * @property visualConfig - Visual rendering configuration for this person
 * @property gestureState - Gesture recognition state for this person
 * @property trailBuffer - Motion trail history for this person
 * @property motionTracker - Velocity and acceleration tracking for this person
 * @property zDepth - Average Z-depth for occlusion ordering
 * @property lastSeenFrame - Frame number when person was last matched (for timeout tracking)
 * @property visualizationType - Assigned visualization style for this person
 */
export interface PersonData {
  personId: PersonID;
  poseData: PoseData;
  visualConfig: VisualConfig;
  gestureState: GestureState;
  trailBuffer: TrailPoint[];
  motionTracker: MotionTracker;
  zDepth: number;
  lastSeenFrame: number;
  visualizationType: VisualizationType;
}

/**
 * Collection of all tracked people.
 * Manages the set of currently detected and tracked individuals.
 * 
 * @property people - Map of PersonID to PersonData for all tracked people
 * @property frameCount - Current frame number for timeout tracking
 */
export interface PersonCollection {
  people: Map<PersonID, PersonData>;
  frameCount: number;
}

/**
 * Configuration for multi-person tracking system.
 * 
 * @property maxPeople - Soft limit for number of people to track (2 for MVP, extensible to 4+)
 * @property matchingThreshold - Distance threshold for person matching in normalized coordinates (0.15)
 * @property timeoutFrames - Number of frames before removing inactive person (30)
 */
export interface MultiPersonConfig {
  maxPeople: number;
  matchingThreshold: number;
  timeoutFrames: number;
}

/**
 * Gesture types supported by the application.
 * Each gesture triggers specific actions or visual feedback.
 * 
 * - ARMS_RAISED: Both arms raised above shoulders (cycles color palette)
 * - ARMS_CROSSED: Arms crossed at chest level (toggles display mode)
 * - LEFT_ARM_EXTENDED: Left arm extended horizontally (decreases trail length)
 * - RIGHT_ARM_EXTENDED: Right arm extended horizontally (increases trail length)
 * - LEFT_ARM_RAISED: Left arm raised vertically (cycles visualization style backward, per person)
 * - RIGHT_ARM_RAISED: Right arm raised vertically (cycles visualization style forward, per person)
 */
export enum GestureType {
  ARMS_RAISED = 'ARMS_RAISED',
  ARMS_CROSSED = 'ARMS_CROSSED',
  LEFT_ARM_EXTENDED = 'LEFT_ARM_EXTENDED',
  RIGHT_ARM_EXTENDED = 'RIGHT_ARM_EXTENDED',
  LEFT_ARM_RAISED = 'LEFT_ARM_RAISED',
  RIGHT_ARM_RAISED = 'RIGHT_ARM_RAISED',
}

/**
 * Gesture event emitted when a gesture is detected and passes debouncing.
 * Used for triggering actions (color changes, mode toggles, etc.).
 * 
 * @property type - The type of gesture detected
 * @property confidence - Detection confidence score [0, 1]
 * @property timestamp - Time when gesture was detected (milliseconds since epoch)
 */
export interface GestureEvent {
  type: GestureType;
  confidence: number;
  timestamp: number;
}

/**
 * Display modes for the application.
 * 
 * - POSE_ANALYSIS: Technical view with labeled landmarks, gesture indicators, FPS counter, and camera preview
 * - ARTISTIC: Clean, immersive view with just the visualization (no labels or technical overlays)
 */
export enum DisplayMode {
  POSE_ANALYSIS = 'POSE_ANALYSIS',
  ARTISTIC = 'ARTISTIC',
}

/**
 * RGBA color representation.
 * 
 * @property r - Red channel [0-255]
 * @property g - Green channel [0-255]
 * @property b - Blue channel [0-255]
 * @property a - Alpha (opacity) channel [0-1]
 */
export interface Color {
  r: number;
  g: number;
  b: number;
  a: number;
}

/**
 * Visual configuration for rendering.
 * Controls colors, trail effects, and line thickness.
 * 
 * @property primaryColor - Main color for stick figure and trails
 * @property secondaryColor - Secondary color for accents
 * @property trailLength - Number of frames to keep in motion trail history (10-60)
 * @property trailOpacity - Base opacity for trail rendering [0-1]
 * @property stickFigureThickness - Line thickness for stick figure rendering (pixels)
 */
export interface VisualConfig {
  primaryColor: Color;
  secondaryColor: Color;
  trailLength: number;
  trailOpacity: number;
  stickFigureThickness: number;
}

/**
 * Central application state.
 * Immutable state pattern - always create new state objects on updates.
 * 
 * @property displayMode - Current display mode (POSE_ANALYSIS or ARTISTIC)
 * @property visualConfig - Current visual rendering configuration (shared defaults / quality settings)
 * @property debugOverlayEnabled - Whether debug overlay is enabled (only visible in ARTISTIC mode)
 * @property people - Collection of all tracked people (each with its own pose and gesture state)
 */
export interface AppState {
  displayMode: DisplayMode;
  visualConfig: VisualConfig;
  debugOverlayEnabled: boolean;
  people: PersonCollection;
}

/**
 * Pose Detection Module Interface.
 * Manages webcam access and MediaPipe pose detection.
 * Runs detection at 30fps target framerate with temporal smoothing.
 * Supports multi-person detection (returns arrays of poses).
 */
export interface IPoseDetector {
  /**
   * Initialize MediaPipe pose detection model.
   * Must be called before start().
   * @param config Optional configuration including numPoses for multi-person detection
   */
  initialize(config?: { numPoses?: number }): Promise<void>;
  
  /**
   * Start pose detection from webcam.
   * Requests camera permissions if not already granted.
   */
  start(): Promise<void>;
  
  /**
   * Stop pose detection and release camera.
   */
  stop(): void;
  
  /**
   * Get all currently detected poses (multi-person mode).
   * @returns Array of detected poses (empty array if no poses detected)
   */
  getCurrentPoses(): PoseData[];
  
  /**
   * Register callback for pose updates (multi-person mode).
   * Callback is invoked every time new poses are detected (~30fps).
   * @param callback Function to call with array of new pose data
   */
  onPosesUpdate(callback: (poses: PoseData[]) => void): void;
}

/**
 * Gesture Recognition Module Interface.
 * Provides two detection paths:
 * 1. detectActiveGestures() - Continuous detection for display (no debouncing)
 * 2. analyzePose() - Debounced detection for actions (color change, mode toggle, etc.)
 * 
 * This separation ensures gesture names display continuously while preventing
 * rapid action firing.
 * 
 * Supports per-person gesture state via an optional personId argument.
 * 
 * @example
 * // For continuous display of gesture names
 * const activeGestures = recognizer.detectActiveGestures(pose, personId);
 * personData.gestureState.activeGestures = new Set(activeGestures);
 * 
 * @example
 * // For triggering actions (debounced)
 * const gestureEvents = recognizer.analyzePose(pose, personId);
 * gestureEvents.forEach(event => stateManager.handleGesture(event, personId));
 * 
 * Validates Requirements 3.3.9
 */
export interface IGestureRecognizer {
  /**
   * Detect all currently active gestures without debouncing.
   * Used for continuous display of gesture names in the UI.
   * Returns all detected gestures every frame, regardless of cooldown state.
   * 
   * Gesture priority rules are applied:
   * - If ARMS_RAISED is detected, LEFT_ARM_RAISED and RIGHT_ARM_RAISED are suppressed
   * 
   * @param pose Current pose data
   * @param personId Optional person identifier for multi-person mode
   * @returns Array of all currently detected gesture types
   */
  detectActiveGestures(pose: PoseData, personId?: PersonID): GestureType[];
  
  /**
   * Analyze pose data for debounced gesture actions.
   * Used for triggering state changes (color change, mode toggle, etc.).
   * Applies debouncing: gestures must be held for 500ms to trigger,
   * then have a 1000ms cooldown before triggering again.
   * 
   * @param pose Current pose data
   * @param personId Optional person identifier for multi-person mode
   * @returns Array of detected gesture events (after debouncing)
   */
  analyzePose(pose: PoseData, personId?: PersonID): GestureEvent[];
  
  /**
   * Reset all gesture debounce states.
   * Useful when switching modes or restarting detection.
   */
  reset(): void;
  
  /**
   * Reset gesture state for a specific person.
   * Called when a person exits the frame in multi-person mode.
   * @param personId Person identifier to reset
   */
  resetPerson(personId: PersonID): void;
}

/**
 * State Management Module Interface.
 * Central state store using immutable state pattern.
 * Coordinates data flow between modules and manages gesture-to-action mappings.
 * Supports both single-person and multi-person modes.
 */
export interface IStateManager {
  /**
   * Get current application state.
   * @returns Current immutable state object
   */
  getState(): AppState;
  
  /**
   * Update application state with partial changes.
   * Creates new state object (immutable pattern).
   * Notifies all subscribers of state change.
   * @param partial Partial state object with fields to update
   */
  setState(partial: Partial<AppState>): void;
  
  /**
   * Subscribe to state changes.
   * Callback is invoked whenever state is updated.
   * @param callback Function to call with new state
   * @returns Unsubscribe function
   */
  subscribe(callback: (state: AppState) => void): () => void;
  
  /**
   * Toggle between POSE_ANALYSIS and ARTISTIC display modes.
   */
  toggleDisplayMode(): void;
  
  /**
   * Handle a gesture event and apply corresponding action.
   * Maps gestures to state changes (color cycle, mode toggle, trail length, etc.).
   * @param gesture Gesture event to handle
   * @param personId Optional person identifier for multi-person mode
   */
  handleGesture(gesture: GestureEvent, personId?: PersonID): void;
  
  /**
   * Toggle debug overlay on/off.
   * Debug overlay shows semi-transparent stick figure over artistic visualizations.
   * Only visible in ARTISTIC mode.
   */
  toggleDebugOverlay(): void;
  
  /**
   * Set debug overlay state explicitly.
   * @param enabled Whether debug overlay should be enabled
   */
  setDebugOverlay(enabled: boolean): void;
  
  /**
   * Get person collection (multi-person mode).
   * @returns Current person collection
   */
  getPeople(): PersonCollection;
  
  /**
   * Update person collection (multi-person mode).
   * @param people New person collection
   */
  updatePeople(people: PersonCollection): void;
}

/**
 * Animation Engine Module Interface.
 * Handles all visual rendering using p5.js.
 * Supports two display modes with different rendering styles.
 */
export interface IAnimationEngine {
  /**
   * Initialize p5.js and set up rendering context.
   * p5 is loaded dynamically, so initialization is asynchronous.
   * @param canvas HTML canvas element to render to
   */
  initialize(canvas: HTMLCanvasElement): void | Promise<void>;
  
  /**
   * Render current frame based on application state.
   * Draws stick figure, trails, ambient animation, and UI overlays.
   * @param state Current application state
   */
  render(state: AppState): void;
  
  /**
   * Handle window resize events.
   * Updates canvas dimensions and rendering viewport.
   * @param width New canvas width
   * @param height New canvas height
   */
  resize(width: number, height: number): void;
  
  /**
   * Set video element for camera preview rendering.
   * Preview is shown in POSE_ANALYSIS mode only.
   * @param video HTML video element with webcam stream
   */
  setVideoElement(video: HTMLVideoElement): void;
}

/**
 * Person Tracker Module Interface.
 * Manages person identification and tracking across frames.
 * Matches detected poses to existing people, creates new IDs, and removes timed-out people.
 */
export interface IPersonTracker {
  /**
   * Update tracking with new frame of detected poses.
   * Matches poses to existing people, creates new IDs, removes timed-out people.
   * @param poses Array of detected poses from current frame
   * @param frameNumber Current frame number for timeout tracking
   * @returns Updated person collection
   */
  update(poses: PoseData[], frameNumber: number): PersonCollection;
  
  /**
   * Get current collection of tracked people.
   * @returns Current person collection
   */
  getPeople(): PersonCollection;
  
  /**
   * Reset tracking (clear all people).
   */
  reset(): void;
  
  /**
   * Get person data by ID.
   * @param personId Person identifier to look up
   * @returns Person data if found, undefined otherwise
   */
  getPerson(personId: PersonID): PersonData | undefined;
}
